import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Spinner } from '../components/ui/Spinner.jsx';
import { showToast } from '../components/ui/Toaster.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { financeApi } from '../services/financeApi.js';
import { formatDate, todayIso } from '../utils/formatters.js';

const BASE_CURRENCY = 'CHF';
const RATES_LIMIT = 50;

export function CurrencySettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [newCurrency, setNewCurrency] = useState({ code: '', name: '' });
  const [rateForm, setRateForm] = useState({ currency: '', rateDate: todayIso(), rate: '' });
  const [fetchError, setFetchError] = useState(null);

  const currencies = useQuery({
    queryKey: ['finance', 'currencies'],
    queryFn: () => financeApi.listCurrencies().then((r) => r.items),
  });

  const rates = useQuery({
    queryKey: ['finance', 'exchange-rates'],
    queryFn: () => financeApi.listExchangeRates({ limit: RATES_LIMIT }).then((r) => r.items),
  });

  const invalidateCurrencies = () =>
    queryClient.invalidateQueries({ queryKey: ['finance', 'currencies'] });
  const invalidateRates = () =>
    queryClient.invalidateQueries({ queryKey: ['finance', 'exchange-rates'] });

  const toggleActive = useMutation({
    mutationFn: ({ code, isActive }) => financeApi.setCurrencyActive(code, { isActive }),
    onSuccess: invalidateCurrencies,
  });

  const addCurrency = useMutation({
    mutationFn: (body) => financeApi.createCurrency(body),
    onSuccess: () => {
      invalidateCurrencies();
      setNewCurrency({ code: '', name: '' });
    },
  });

  const saveRate = useMutation({
    mutationFn: (body) => financeApi.upsertExchangeRate(body),
    onSuccess: () => {
      invalidateRates();
      setRateForm({ currency: '', rateDate: todayIso(), rate: '' });
    },
  });

  const removeRate = useMutation({
    mutationFn: (id) => financeApi.deleteExchangeRate(id),
    onSuccess: invalidateRates,
  });

  const fetchRates = useMutation({
    mutationFn: () => financeApi.fetchExchangeRates(),
    onSuccess: (data) => {
      const updated = data?.updated ?? [];
      const failed = data?.failed ?? [];
      showToast(t('currency.rates.fetched', { count: updated.length }), 'success');
      setFetchError(
        failed.length > 0
          ? t('currency.rates.fetchFailed', {
              reason: failed.map((f) => `${f.currency}: ${f.reason}`).join(', '),
            })
          : null,
      );
      invalidateRates();
    },
    onError: (err) =>
      setFetchError(t('currency.rates.fetchFailed', { reason: err?.message || '' })),
  });

  function submitCurrency(e) {
    e.preventDefault();
    if (!newCurrency.code.trim() || !newCurrency.name.trim()) return;
    addCurrency.mutate({ code: newCurrency.code.trim().toUpperCase(), name: newCurrency.name.trim() });
  }

  function submitRate(e) {
    e.preventDefault();
    if (!rateForm.currency || !rateForm.rateDate || !rateForm.rate) return;
    saveRate.mutate({ currency: rateForm.currency, rateDate: rateForm.rateDate, rate: rateForm.rate });
  }

  if (currencies.isLoading || rates.isLoading) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }

  const currencyRows = currencies.data ?? [];
  const rateRows = rates.data ?? [];
  const foreignActive = currencyRows.filter((c) => c.isActive && c.code !== BASE_CURRENCY);

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('currency.col.code')}</th>
                <th>{t('currency.col.name')}</th>
                <th>{t('currency.col.active')}</th>
              </tr>
            </thead>
            <tbody>
              {currencyRows.map((c) => (
                <tr key={c.code}>
                  <td className="mono">{c.code}</td>
                  <td>{c.name}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${t('currency.col.active')} ${c.code}`}
                      checked={Boolean(c.isActive)}
                      disabled={c.code === BASE_CURRENCY || toggleActive.isPending}
                      onChange={(e) =>
                        toggleActive.mutate({ code: c.code, isActive: e.target.checked })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('currency.baseNote')}</p>

        <form onSubmit={submitCurrency} className="field-row" style={{ alignItems: 'flex-end' }}>
          <label className="field">
            <span className="field-label">{t('currency.col.code')}</span>
            <input
              className="input"
              value={newCurrency.code}
              maxLength={3}
              style={{ textTransform: 'uppercase' }}
              onChange={(e) => setNewCurrency((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
            />
          </label>
          <label className="field">
            <span className="field-label">{t('currency.col.name')}</span>
            <input
              className="input"
              value={newCurrency.name}
              onChange={(e) => setNewCurrency((p) => ({ ...p, name: e.target.value }))}
            />
          </label>
          <Button type="submit" disabled={addCurrency.isPending}>
            {t('currency.add')}
          </Button>
        </form>
        {addCurrency.isError && (
          <div className="alert danger">{addCurrency.error?.message || t('common.loadError')}</div>
        )}

        <div className="settings-divider">{t('currency.rates.title')}</div>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('currency.rates.intro')}</p>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('invoice.fx.currency')}</th>
                <th>{t('currency.rates.date')}</th>
                <th className="num">{t('currency.rates.rate')}</th>
                <th>{t('currency.rates.source')}</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {rateRows.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.currency}</td>
                  <td>{formatDate(r.rateDate)}</td>
                  <td className="num mono">{r.rate}</td>
                  <td>
                    <Badge variant="neutral">{r.source}</Badge>
                  </td>
                  <td className="num">
                    <Button
                      variant="ghost"
                      disabled={removeRate.isPending}
                      onClick={() => removeRate.mutate(r.id)}
                    >
                      {t('common.delete')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <form onSubmit={submitRate} className="field-row" style={{ alignItems: 'flex-end' }}>
          <label className="field">
            <span className="field-label">{t('invoice.fx.currency')}</span>
            <select
              className="select"
              value={rateForm.currency}
              onChange={(e) => setRateForm((p) => ({ ...p, currency: e.target.value }))}
            >
              <option value="">-</option>
              {foreignActive.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('currency.rates.date')}</span>
            <input
              className="input"
              type="date"
              value={rateForm.rateDate}
              onChange={(e) => setRateForm((p) => ({ ...p, rateDate: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">{t('currency.rates.rate')}</span>
            <input
              className="input"
              type="number"
              step="0.000001"
              min="0.000001"
              value={rateForm.rate}
              onChange={(e) => setRateForm((p) => ({ ...p, rate: e.target.value }))}
            />
          </label>
          <Button type="submit" disabled={saveRate.isPending}>
            {t('currency.rates.add')}
          </Button>
        </form>
        {saveRate.isError && (
          <div className="alert danger">{saveRate.error?.message || t('common.loadError')}</div>
        )}
        {removeRate.isError && (
          <div className="alert danger">{removeRate.error?.message || t('common.loadError')}</div>
        )}
        {fetchError && <div className="alert danger">{fetchError}</div>}

        <div className="page-actions">
          <Button variant="primary" disabled={fetchRates.isPending} onClick={() => fetchRates.mutate()}>
            {fetchRates.isPending ? `${t('currency.rates.fetch')}…` : t('currency.rates.fetch')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
